import type { FC, ReactNode } from "react";
import { InfoCircle } from "@untitledui/icons";
import { Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";

interface FormGroupProps {
    title: string;
    description?: string;
    /** Leading glyph next to the title (Premium Selection cards). */
    icon?: FC<{ className?: string }>;
    /** Help text behind an ⓘ immediately after the title. The Invoice
     *  form uses it to say where each pre-filled card is actually edited,
     *  which is the only explanation the locked fields get. Distinct from
     *  `action`, which sits at the far right of the header. */
    titleTooltip?: string;
    action?: ReactNode;
    /** Render the body with zero padding — useful when the child is a full-width Table. */
    flushBody?: boolean;
    /** Rule between the header and the body. Defaults to true; the approved
     *  Premium Selection design (OR-1522) draws the Primary Premium Walk card
     *  without it. */
    headerDivider?: boolean;
    /** Extra classes on the card itself — e.g. `grow` to make the last card in
     *  a column absorb the slack so two side-by-side columns end level. */
    className?: string;
    children: ReactNode;
    /** Custom attribute for E2E testing — applied to the card's outer `<section>`. */
    "data-autotest"?: string;
}

export function FormGroup({
    title,
    description,
    icon: Icon,
    titleTooltip,
    action,
    flushBody,
    headerDivider = true,
    className,
    children,
    "data-autotest": dataAutotest,
}: FormGroupProps) {
    return (
        <section
            data-autotest={dataAutotest}
            className={cx("border-secondary bg-primary shadow-xs overflow-hidden rounded-xl border", className)}
        >
            <header
                className={cx(
                    "border-secondary flex items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4",
                    headerDivider && "border-b",
                )}
            >
                <div>
                    <h3 className="text-primary sm:text-md flex items-center gap-2 text-sm font-semibold">
                        {Icon && <Icon aria-hidden="true" className="text-fg-brand-primary size-5" />}
                        {title}
                        {titleTooltip && (
                            <Tooltip title={titleTooltip} placement="top">
                                <TooltipTrigger aria-label={`About ${title}`} className="cursor-help align-middle">
                                    <InfoCircle aria-hidden="true" className="text-fg-quaternary size-4" />
                                </TooltipTrigger>
                            </Tooltip>
                        )}
                    </h3>
                    {description && <p className="text-tertiary mt-1 text-xs sm:text-sm">{description}</p>}
                </div>
                {action}
            </header>
            <div className={flushBody ? "" : "flex flex-col gap-4 p-4 sm:gap-5 sm:p-6"}>{children}</div>
        </section>
    );
}
